import React, { useEffect, useMemo, useState } from "react";
import { MenuItem, Select } from "@mui/material";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import { extractFilterListPayload } from "../../hooks/useAdvancedSearchFilterOptions";
import { useCachedFilterOptions } from "../../hooks/useCachedFilterOptions";
import {
  EMPTY_SURVEYS_FILTER_STATE,
  normalizeSurveysFilterState,
} from "../../utils/dashboardSurveysFilterState";
import DashboardSavedFiltersPanel from "./DashboardSavedFiltersPanel";
import {
  DashboardFilterModalActions,
  DashboardFilterModalFrame,
  DashboardFilterSection,
  DashboardMultiPickList,
} from "./dashboardFilterModalUi";

const CHANNEL_OPTIONS = [
  "whatsapp",
  "web",
  "email",
  "sms",
  "chat",
  "api",
  "survey",
  "channel",
  "legacy",
];

async function fetchSurveysFilterOptions() {
  const ch = await fetchData("/channels?limit=200", "GET");
  return { channels: extractFilterListPayload(ch) };
}

export default function DashboardSurveysFiltersModal({
  open,
  onClose,
  initialState = EMPTY_SURVEYS_FILTER_STATE,
  onApply,
}) {
  const { t } = useTranslation("settings");
  const { data: options, isLoading: loading } = useCachedFilterOptions(
    "dashboard-surveys",
    fetchSurveysFilterOptions,
    { enabled: open }
  );
  const channels = options?.channels || [];
  const [channel, setChannel] = useState("");
  const [originIds, setOriginIds] = useState([]);
  const prevOpen = React.useRef(false);

  const channelOptions = useMemo(() => CHANNEL_OPTIONS, []);

  useEffect(() => {
    if (!open) {
      prevOpen.current = false;
      return;
    }
    const opening = !prevOpen.current;
    prevOpen.current = true;
    if (opening) {
      const s = normalizeSurveysFilterState(initialState);
      setChannel(s.channel);
      setOriginIds(s.ids_origens);
    }
  }, [open, initialState]);

  const buildLabels = () => {
    const channelLabels = {};
    channelOptions.forEach((c) => {
      channelLabels[c] = t(`dashboardPage.surveysFilters.channel_${c}`, { defaultValue: c });
    });
    const origins = {};
    channels.forEach((c) => {
      const id = Number(c?.id);
      if (!Number.isFinite(id)) return;
      origins[String(id)] = String(c?.nome || c?.name || `#${id}`);
    });
    return { channels: channelLabels, origins };
  };

  const handleClear = () => {
    setChannel("");
    setOriginIds([]);
  };

  const handleApply = () => {
    onApply?.(
      normalizeSurveysFilterState({
        channel,
        ids_origens: originIds,
        labels: buildLabels(),
      })
    );
    onClose?.();
  };

  const hasActive = Boolean(channel) || originIds.length > 0;

  return (
    <DashboardFilterModalFrame
      open={open}
      onClose={onClose}
      icon={<PollOutlinedIcon />}
      title={t("dashboardPage.surveysFilters.title", { defaultValue: "Filtrar pesquisa" })}
      subtitle={t("dashboardPage.surveysFilters.subtitle", {
        defaultValue: "As respostas analisadas passam a considerar só estas origens",
      })}
      loading={loading}
      footer={
        <DashboardFilterModalActions
          onClear={handleClear}
          onClose={onClose}
          onApply={handleApply}
          hasActive={hasActive}
          clearLabel={t("dashboardPage.surveysFilters.clearAll", { defaultValue: "Limpar" })}
          cancelLabel={t("dashboardPage.surveysFilters.cancel", { defaultValue: "Cancelar" })}
          applyLabel={t("dashboardPage.surveysFilters.apply", { defaultValue: "Aplicar" })}
        />
      }
    >
      <DashboardFilterSection
        title={t("dashboardPage.surveysFilters.channel", { defaultValue: "Canal da sessão" })}
        hint={t("dashboardPage.surveysFilters.channelHint", {
          defaultValue: "Como a pesquisa foi respondida",
        })}
      >
        <Select
          fullWidth
          size="small"
          displayEmpty
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="">
            {t("dashboardPage.surveysFilters.channelAll", { defaultValue: "Qualquer" })}
          </MenuItem>
          {channelOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {t(`dashboardPage.surveysFilters.channel_${opt}`, { defaultValue: opt })}
            </MenuItem>
          ))}
        </Select>
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.surveysFilters.origins", { defaultValue: "Origem do lead" })}
        selectedCount={originIds.length}
      >
        <DashboardMultiPickList
          options={channels}
          value={originIds}
          onChange={setOriginIds}
          getLabel={(o) => o?.nome || o?.name || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.surveysFilters.searchOrigins", {
            defaultValue: "Buscar origens…",
          })}
          emptyLabel={t("dashboardPage.surveysFilters.emptyOrigins", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      {!loading ? (
        <DashboardSavedFiltersPanel
          section="surveys"
          getSnapshot={() =>
            normalizeSurveysFilterState({
              channel,
              ids_origens: originIds,
              labels: buildLabels(),
            })
          }
          onApply={(state) => {
            onApply?.(state);
            onClose?.();
          }}
          canSave
        />
      ) : null}
    </DashboardFilterModalFrame>
  );
}
